/**
 * VersionHistoryModal (NQG-RQ-27)
 *
 * Given a question id (original OR any version — the backend resolves to the
 * root), fetches the version tree and shows the Original (Q1) plus all
 * versions (Q1.1, Q1.2...) with category/difficulty badges (AC-1), the
 * original clearly marked and always accessible (AC-2), and per-version
 * date, author, category and description (AC-3). Selecting an entry opens
 * the question in the view modal (when provided) or the edit page.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  GitBranch,
  Loader2,
  X,
  AlertCircle,
  Eye,
  Edit3,
  Calendar,
  User,
  Star,
} from 'lucide-react';
import { StatusBadge } from '../Badge';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import type { VersionHistoryEntry } from '../../types/pdf-analysis.types';

interface VersionHistoryModalProps {
  /** Id of the original question or any of its versions */
  questionId: string;
  canEdit?: boolean;
  onClose: () => void;
  /** When provided, "View" opens this callback with the full question doc (view modal pattern) */
  onView?: (question: any) => void;
}

export function VersionHistoryModal({
  questionId,
  canEdit = false,
  onClose,
  onView,
}: VersionHistoryModalProps) {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['question-versions', questionId],
    queryFn: () => pdfAnalysisService.getQuestionVersions(questionId),
  });

  // Config gives the display labels for difficulty keys (config-based, AC-4)
  const { data: config } = useQuery({
    queryKey: ['version-config'],
    queryFn: () => pdfAnalysisService.getVersionConfig(),
    staleTime: 5 * 60 * 1000,
  });

  const difficultyLabel = (key?: string) => {
    if (!key) return null;
    return config?.difficulties.find((d) => d.key === key)?.label || key;
  };

  const allDocs = data ? [data.root, ...(data.versions || [])] : [];
  const findDoc = (id: string) => allDocs.find((d) => d?._id === id);

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleOpen = (entry: VersionHistoryEntry) => {
    const doc = findDoc(entry.questionId);
    if (onView && doc) {
      onView(doc);
      return;
    }
    navigate(`/pdf-analysis/questions/${entry.questionId}/edit`);
    onClose();
  };

  const handleEdit = (entry: VersionHistoryEntry) => {
    navigate(`/pdf-analysis/questions/${entry.questionId}/edit`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e73be] to-[#4A90C8] px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <GitBranch className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Version History</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  {data?.root?.oeCode && (
                    <span className="text-xs font-mono font-bold text-white/80 bg-white/20 px-2 py-0.5 rounded">
                      {data.root.oeCode}
                    </span>
                  )}
                  {data && (
                    <span className="text-xs text-white/60">
                      {data.versions?.length || 0} version{(data.versions?.length || 0) !== 1 ? 's' : ''} of the original
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 gap-3 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin text-[#1e73be]" />
              <span className="text-sm font-medium">Loading versions...</span>
            </div>
          ) : error || !data ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              Failed to load version history. Please try again.
            </div>
          ) : (
            <div className="space-y-3">
              {data.history.map((entry) => (
                <div
                  key={entry.questionId}
                  className={`rounded-xl border-2 p-4 transition-all hover:shadow-md ${
                    entry.isOriginal
                      ? 'border-blue-300 bg-gradient-to-r from-blue-50 to-blue-50/30'
                      : 'border-gray-200 bg-white hover:border-purple-300'
                  }`}
                >
                  {/* Top row: label + badges + actions */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold border ${
                          entry.isOriginal
                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                            : 'bg-purple-50 text-purple-700 border-purple-200'
                        }`}
                      >
                        {entry.isOriginal ? <Star className="w-3 h-3" /> : <GitBranch className="w-3 h-3" />}
                        {entry.label}
                      </span>
                      {entry.isOriginal && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700 bg-blue-100 border border-blue-300 px-1.5 py-0.5 rounded">
                          Original
                        </span>
                      )}
                      {entry.categoryLabel && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                          {entry.categoryLabel}
                        </span>
                      )}
                      {entry.difficulty && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                          {difficultyLabel(entry.difficulty)}
                        </span>
                      )}
                      <StatusBadge status={entry.status} />
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleOpen(entry)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View question"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(entry)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit question"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Stem preview */}
                  <p className="text-sm text-gray-800 mt-2 line-clamp-2">
                    {entry.stemPreview}
                    {entry.stemPreview?.length >= 160 ? '…' : ''}
                  </p>

                  {/* Description (user note) */}
                  {entry.description && (
                    <p className="text-xs text-gray-500 italic mt-1">"{entry.description}"</p>
                  )}

                  {/* Meta: date + author (AC-3) */}
                  <div className="flex items-center gap-4 text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(entry.createdAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {entry.author}
                    </span>
                  </div>
                </div>
              ))}

              {data.history.length === 1 && (
                <p className="text-center text-sm text-gray-400 py-2">
                  No versions yet — only the original question.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary flex-1 py-2.5 rounded-xl text-sm font-semibold">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default VersionHistoryModal;
